import { useCallback, useEffect, useState } from "react";
import { Modal, Row, Col, Spinner, Badge, ListGroup } from "react-bootstrap";
import { Table, Input } from "antd";
import Swal from "sweetalert2";
import { ListadoPlanes } from "../../../Service/Service.Plan";
import { GenerarCoberturaSeguro } from "../../../Service/Service.porcedimientos";

// COBERTURAS-09B -- selección explícita de plan y generación de la cobertura
// 100 %. El backend resuelve precio_paciente / precio_empresa server-side; este
// modal solo envía (idprocedimiento_cita_paciente, id_plan).
function ModalGenerarCobertura({ data }) {
    const { show, handleClose, row, onSuccess } = data;

    const [loading, setLoading] = useState(false);
    const [planes, setPlanes] = useState([]);
    const [selected, setSelected] = useState(null);
    const [search, setSearch] = useState("");

    const fetchPlanes = useCallback(async () => {
        if (!row) return;
        setLoading(true);
        // CONFIG-PLANES-SEGURO-31: nueva cobertura -> solo planes Activos.
        const res = await ListadoPlanes({ estado: "Activo", seguro_id: row.seguro_id });
        const lista = (res?.data || [])
            .filter((p) => p.seguro_id === row.seguro_id)
            .map((p) => {
                const detalle = (p.detalles || []).find(
                    (d) => d.id_tipo_examen === row.idExamenTipo && d.estado !== "Inactivo"
                );
                return { ...p, key: p.id_plan, detalleServicio: detalle || null };
            });
        setPlanes(lista);
        setSelected(null);
        setLoading(false);
    }, [row]);

    useEffect(() => {
        if (show) {
            setSearch("");
            fetchPlanes();
        }
    }, [show, fetchPlanes]);

    const handleGenerar = async () => {
        if (!selected) return;
        setLoading(true);
        const res = await GenerarCoberturaSeguro({
            idprocedimiento_cita_paciente: row.idprocedimiento_cita_paciente,
            id_plan: selected.id_plan,
        });
        setLoading(false);

        if (res.ok) {
            handleClose();
            onSuccess && onSuccess();
            Swal.fire({
                icon: res.code === "COBERTURA_GENERADA" ? "success" : "info",
                title: res.code === "COBERTURA_GENERADA" ? "Cobertura generada" : "Sin cambios",
                text: res.message,
            });
            return;
        }

        if (res.code === "REQUIERE_FACTURACION_PACIENTE") {
            Swal.fire({
                icon: "warning",
                title: "Esta prestación tiene copago",
                html:
                    (res.message || "") +
                    (res.extra
                        ? `<br/><small>Paciente: $${Number(res.extra.precio_paciente).toFixed(2)} · Seguro: $${Number(res.extra.precio_empresa).toFixed(2)}</small>`
                        : ""),
            });
            return;
        }

        Swal.fire({ icon: "error", title: "No se pudo generar", text: res.message });
    };

    const filtered = planes.filter(
        (p) => !search || (p.nombre || "").toLowerCase().includes(search.toLowerCase())
    );

    const columns = [
        { title: "Plan", dataIndex: "nombre", key: "nombre", width: 240 },
        {
            title: "Cobertura para este servicio",
            key: "detalle",
            render: (_, p) =>
                p.detalleServicio ? (
                    <span>
                        Copago {p.detalleServicio.copago}% · Paciente $
                        {Number(p.detalleServicio.precio_paciente).toFixed(2)} · Seguro $
                        {Number(p.detalleServicio.precio_empresa).toFixed(2)}{" "}
                        {Number(p.detalleServicio.precio_paciente) === 0 &&
                        Number(p.detalleServicio.precio_empresa) > 0 ? (
                            <Badge bg="success">Cobertura 100%</Badge>
                        ) : Number(p.detalleServicio.precio_paciente) > 0 ? (
                            <Badge bg="warning">Copago</Badge>
                        ) : (
                            <Badge bg="secondary">Sin cobertura</Badge>
                        )}
                    </span>
                ) : (
                    <Badge bg="light" text="dark">
                        El plan no cubre este servicio
                    </Badge>
                ),
        },
    ];

    return (
        <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Generar cobertura de seguro</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {row && (
                    <ListGroup className="mb-3">
                        <ListGroup.Item>
                            <b>Paciente:</b> {row.paciente} &nbsp;·&nbsp; <b>Seguro:</b> {row.seguro}
                        </ListGroup.Item>
                        <ListGroup.Item>
                            <b>Servicio:</b> {row.servicio_codigo ? row.servicio_codigo + " - " : ""}
                            {row.servicio} &nbsp;·&nbsp; <b>Precio actual:</b> ${Number(row.precio).toFixed(2)}
                        </ListGroup.Item>
                    </ListGroup>
                )}

                <Row className="mb-2">
                    <Col>
                        <Input
                            placeholder="Buscar plan por nombre"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            allowClear
                        />
                    </Col>
                </Row>

                <Table
                    size="small"
                    loading={loading}
                    columns={columns}
                    dataSource={filtered}
                    pagination={{ pageSize: 8 }}
                    rowSelection={{
                        type: "radio",
                        onChange: (_, rows) => setSelected(rows[0] || null),
                    }}
                    scroll={{ x: "max-content" }}
                />

                <small className="text-muted">
                    El sistema validará la cobertura. Solo se genera la responsabilidad del seguro cuando el
                    plan cubre el 100 % de este servicio (paciente $0). Si hay copago, debe facturarse al
                    paciente por el flujo normal.
                </small>
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-light btn-sm" onClick={handleClose} disabled={loading}>
                    Cancelar
                </button>
                <button
                    className="btn btn-primary btn-sm"
                    onClick={handleGenerar}
                    disabled={loading || !selected}
                >
                    {loading ? (
                        <>
                            <Spinner as="span" animation="grow" size="sm" /> Generando...
                        </>
                    ) : (
                        "Generar cobertura de seguro"
                    )}
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default ModalGenerarCobertura;
